import React, { useMemo } from "react";
import CountUp from "react-countup";
import BaseChart from "../../components/ui/BaseChart";

const CARDS = [
  {
    key: "totalJobs",
    label: "Total Jobs",
    subLabel: "All time",
    icon: "fa-solid fa-briefcase",
    from: "#4F7FEF",
    to: "#7B67E8",
  },
  {
    key: "completed",
    label: "Completed",
    subLabel: "All time",
    icon: "fa-solid fa-circle-check",
    from: "#22C78E",
    to: "#0EA86E",
  },
  {
    key: "ongoing",
    label: "Active Jobs",
    subLabel: "Right now",
    icon: "fa-solid fa-bolt",
    from: "#F59E0B",
    to: "#F97316",
    live: true,
  },
  {
    key: "totalHoursWorked",
    label: "Hours Worked",
    subLabel: "Total logged",
    icon: "fa-solid fa-clock",
    from: "#E879A0",
    to: "#A855F7",
    suffix: "h",
    decimals: 0,
  },
  {
    key: "cancelled",
    label: "Cancelled",
    subLabel: "All time",
    icon: "fa-solid fa-ban",
    from: "#F16B6B",
    to: "#E11D48",
  },
];

const Sparkline = ({ data, color }) => {
  const options = useMemo(
    () => ({
      chart: {
        type: "area",
        height: 56,
        margin: [0, 0, 0, 0],
        backgroundColor: "transparent",
        animation: { duration: 1200, easing: "easeOut" },
      },
      title: { text: null },
      xAxis: { visible: false },
      yAxis: { visible: false, min: 0 },
      legend: { enabled: false },
      tooltip: { enabled: false },
      plotOptions: {
        area: {
          marker: { enabled: false },
          lineWidth: 2.5,
          lineColor: color,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, `${color}55`],
              [1, `${color}00`],
            ],
          },
          states: { hover: { enabled: false } },
        },
      },
      series: [{ name: "Trend", data: data?.length ? data : Array(8).fill(0) }],
    }),
    [data, color],
  );
  return <BaseChart options={options} style={{ width: "100%", height: "56px" }} />;
};

const KPI_STYLES = `
  @keyframes kpiIn {
    from { opacity: 0; transform: translateY(24px) scale(0.94); }
    to   { opacity: 1; transform: translateY(0)    scale(1);    }
  }
  @keyframes bgPan {
    0%   { background-position: 0% 50%;   }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%;   }
  }
  @keyframes blob1 {
    0%,100% { transform: translate(0px,  0px)  scale(1);    }
    33%     { transform: translate(-10px,-14px) scale(1.08); }
    66%     { transform: translate(8px,  -6px)  scale(0.94); }
  }
  @keyframes blob2 {
    0%,100% { transform: translate(0px, 0px)  scale(1);    }
    33%     { transform: translate(12px, 10px) scale(1.1);  }
    66%     { transform: translate(-6px, 7px)  scale(0.92); }
  }
  @keyframes livePulse {
    0%,100% { transform: scale(1);    opacity: 1;   }
    50%     { transform: scale(1.7);  opacity: 0.3; }
  }
  .kpi-card {
    animation: kpiIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
                box-shadow 0.28s ease;
  }
  .kpi-card:hover {
    transform: translateY(-6px) scale(1.02);
    box-shadow: 0 24px 48px -8px rgba(0,0,0,0.28) !important;
  }
  .kpi-icon-wrap {
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .kpi-card:hover .kpi-icon-wrap {
    transform: scale(1.15) rotate(-8deg);
  }
  .kpi-blob1 {
    animation: blob1 7s ease-in-out infinite;
  }
  .kpi-blob2 {
    animation: blob2 9s ease-in-out infinite;
  }
  .live-dot {
    animation: livePulse 1.8s ease-in-out infinite;
  }
`;

const EngineerKpiCards = ({ data }) => (
  <>
    <style>{KPI_STYLES}</style>
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
      {CARDS?.map((card, i) => {
        const kpi = data?.[card.key];
        const val = kpi?.value ?? 0;

        return (
          <div
            key={card.key}
            className="kpi-card relative bg-white rounded-2xl overflow-hidden flex flex-col cursor-default border border-gray-100"
            style={{
              animationDelay: `${i * 90}ms, ${i * 90}ms`,
              boxShadow: "0 2px 14px rgba(0,0,0,0.06)",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.boxShadow = `0 16px 40px -6px ${card.from}44, 0 2px 14px rgba(0,0,0,0.06)`)
            }
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 2px 14px rgba(0,0,0,0.06)")}
          >
            {/* Animated gradient top bar */}
            <div
              className="kpi-gradient-bar h-[3px] w-full"
              style={{
                background: `linear-gradient(90deg, ${card.from}, ${card.to}, ${card.from})`,
              }}
            />

            {/* Floating decorative blobs */}
            <div
              className="kpi-blob1 absolute -top-6 -right-6 w-28 h-28 rounded-full pointer-events-none"
              style={{ background: `${card.from}12`, animationDelay: `${i * 0.4}s` }}
            />
            <div
              className="kpi-blob2 absolute -bottom-8 -left-6 w-20 h-20 rounded-full pointer-events-none"
              style={{ background: `${card.to}0e`, animationDelay: `${i * 0.6}s` }}
            />

            <div className="relative z-10 p-4 flex flex-col gap-3 flex-1">
              {/* Icon row */}
              <div className="flex items-start justify-between">
                <div
                  className="kpi-icon-wrap w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${card.from}18`, boxShadow: `0 4px 14px ${card.from}30` }}
                >
                  <i className={`${card.icon} text-[15px]`} style={{ color: card.from }} />
                </div>
              </div>

              {/* Value + labels */}
              <div className="flex-1">
                <p className="text-[34px] font-extrabold leading-none tracking-tight text-gray-800">
                  {card.prefix && (
                    <span className="text-base font-semibold text-gray-400 mr-0.5">
                      {card.prefix}
                    </span>
                  )}
                  {kpi ? (
                    <CountUp
                      key={val}
                      start={0}
                      end={val}
                      duration={2}
                      decimals={card.decimals ?? 0}
                      separator=","
                      enableScrollSpy
                      scrollSpyDelay={i * 90}
                      scrollSpyOnce
                      useEasing
                    />
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                  {card.suffix && (
                    <span className="text-sm font-semibold text-gray-400 ml-1">{card.suffix}</span>
                  )}
                </p>

                <p className="text-sm font-bold text-gray-700 mt-2 tracking-wide">{card.label}</p>
                <p className="text-[11px] text-gray-500 mt-0.5">{card.subLabel}</p>
              </div>

              {/* Sparkline bleeds to edges */}
              {kpi?.sparkline?.length > 0 && (
                <div className="-mx-4 -mb-4">
                  <Sparkline data={kpi.sparkline} color={card.from} />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </>
);

export default EngineerKpiCards;
